import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'translations/hooks';

import {
  MassValidationKeys,
  COLUMN_WIDTH,
} from 'business/shift-report/report/pages/ReportEdit/components/mass-validation-drawer/constants';
import {
  Item,
  MassValidationColumnType,
} from 'business/shift-report/report/pages/ReportEdit/components/mass-validation-drawer/types';
import {
  TaskFullFragment,
  GetShiftReportMetadataQuery,
} from 'generated/graphql';

export const useMassValidationColumns = (
  handleSave: (record: Item) => void,
) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () =>
      [
        {
          title: t('pages.report.massValidation.name'),
          dataIndex: MassValidationKeys.Name,
          key: MassValidationKeys.Name,
          width: COLUMN_WIDTH,
        },
        {
          title: t('pages.report.massValidation.ring'),
          dataIndex: MassValidationKeys.Ring,
          key: MassValidationKeys.Ring,
          editable: 'number',
          width: COLUMN_WIDTH,
        },
        {
          title: t('pages.report.massValidation.start'),
          dataIndex: MassValidationKeys.Start,
          key: MassValidationKeys.Start,
          render: (value) => {
            return dayjs(value).format('HH:mm');
          },
          editable: 'hour',
          width: COLUMN_WIDTH,
        },
        {
          title: t('pages.report.massValidation.end'),
          dataIndex: MassValidationKeys.End,
          key: MassValidationKeys.End,
          render: (value) => {
            return dayjs(value).format('HH:mm');
          },
          editable: 'hour',
          width: COLUMN_WIDTH,
        },
        {
          title: t('pages.report.massValidation.ended'),
          dataIndex: MassValidationKeys.Ended,
          key: MassValidationKeys.Ended,
          render: (value) => {
            return value ? <CheckOutlined /> : <CloseOutlined />;
          },
          editable: 'boolean',
          width: COLUMN_WIDTH,
        },
      ] satisfies MassValidationColumnType[],
    [t],
  );

  return useMemo(() => {
    return columns.map((column) => {
      return {
        ...column,
        onCell: (record) => {
          return {
            record,
            editable: column.editable,
            dataIndex: column.dataIndex,
            handleSave,
          };
        },
      } as MassValidationColumnType;
    });
  }, [columns, handleSave]);
};

const mapTaskToTableItemWithReport =
  (report: NonNullable<GetShiftReportMetadataQuery['shiftReport']>) =>
  (task: TaskFullFragment) =>
    ({
      key: task.id,
      name: task.activity.name,
      ring: task.ring,
      start: dayjs(task.startDate),
      end: dayjs(task.endDate),
      ended: task.ended,
      report: report,
    }) satisfies Item;

export const useTasksAndData = (
  tasks: TaskFullFragment[] | undefined,
  report: NonNullable<GetShiftReportMetadataQuery['shiftReport']>,
) => {
  const mapTaskToTableItem = mapTaskToTableItemWithReport(report);

  const [selectedTasks, setSelectedTasks] = useState<string[] | undefined>([]);
  const [data, setData] = useState(tasks?.map(mapTaskToTableItem));

  useEffect(() => {
    if (tasks?.length !== data?.length) {
      setData(tasks?.map(mapTaskToTableItem));
    }
  }, [tasks, data, setData, mapTaskToTableItem]);

  return { selectedTasks, setSelectedTasks, data, setData };
};
